import { useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { camOn, feedAudible, feedVisible, pipAudible, type StreamSource } from '@rabbitfuel/shared';
import { useRealtime } from '../lib/realtime.tsx';
import { useSubscriptions, useVideoStream } from '../lib/useMedia.ts';
import { useMixedAudio, useMixedElement, useTabRecorder, type RecorderParams } from '../lib/tabRecorder.ts';
import { StreamOverlay, courseChrome, courseFullscreen, isSplit, playerChrome } from '../components/StreamOverlay.tsx';
import { courseOf } from '../lib/course.ts';
import { PlayerBackdrop, backdropChrome } from '../components/PlayerBackdrop.tsx';
import { StandbyCard } from '../components/Standby.tsx';
import { SplitFill } from '../components/SplitFill.tsx';
import {
  AdFrameLayer,
  AdLabel,
  AdSpot,
  adChrome,
  adFrameChrome,
  sponsorBug,
  useAdBreak,
  useAdFrame,
} from '../components/Ads.tsx';
import { lookOf, videoLook } from '../lib/look.ts';

/** Macht den Tab des Renderers in Prozessliste und Log erkennbar. */
const RENDER_TITLE = 'RabbitFuelRender';

/**
 * Die Seite, die der Server sich selbst ansieht.
 *
 * Das ist die Zuschauersicht ohne alles Drumherum: kein Kopf, keine
 * Bedienleiste, kein Chat -- nur Hauptbild, kleines Fenster und die
 * Einblendungen, formatfuellend auf schwarzem Grund. Genau dieses Bild nimmt
 * der Tab von sich selbst auf und schickt es an ffmpeg, das daraus den
 * RTMP-Stream fuer YouTube oder Twitch macht.
 *
 * Der Punkt dabei: der Zuschauer ist der Server. Der Ausgang haengt damit an
 * keinem Browser des Veranstalters -- ein Reload im Regie-Tab, ein
 * geschlossenes Notebook oder ein WLAN-Wechsel dort aendern nichts daran,
 * dass dieser Stream weiterlaeuft.
 */
/**
 * Haelt die Tab-Aufnahme in Bewegung.
 *
 * Chrome liefert bei `getDisplayMedia` auf den eigenen Tab nur dann ein Bild,
 * wenn sich etwas geaendert hat -- die Bildrate ist eine Obergrenze, keine
 * Zusage. Bei einem *stehenden* Bild liefert die Aufnahme deshalb gar nichts
 * mehr, und genau das ist kein Ausnahmefall: die Regie startet den Ausgang
 * gern, bevor die erste Kamera steht, und beim Abblenden steht das
 * Standardbild ebenfalls still.
 *
 * Gemessen mit stehendem Bild, Ausgang auf einen lokalen RTMP-Empfaenger:
 *
 * |                          | mit Puls | ohne Puls |
 * | Ausgang meldet „live"    |    3,0 s |     9,3 s |
 * | Daten aus dem Renderer   |  6 KB/s  |   0 KB/s  |
 *
 * Der Stream reiszt ohne Puls trotzdem nicht ab -- ffmpeg wiederholt das
 * letzte Bild, angetrieben von der Tonspur, und schreibt weiter saubere
 * 30 fps. Was er kostet, ist die *Startzeit*: bis der `MediaRecorder` sein
 * erstes Bild hat, laeuft nichts, und genauso lange dauert es, bis ein
 * abgelehnter Streamschluessel auffaellt.
 *
 * Zwei Pixel in der Ecke, die pro Bild zwischen zwei benachbarten Schwarztoenen
 * wechseln, genuegen als Anlass. Im Stream ist das nicht zu sehen -- in der
 * Startzeit schon.
 */
function RenderPulse() {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const context = ref.current?.getContext('2d');
    if (!context) return;
    let frame = 0;
    let odd = false;
    const paint = () => {
      odd = !odd;
      context.fillStyle = odd ? '#000000' : '#010101';
      context.fillRect(0, 0, 2, 2);
      frame = requestAnimationFrame(paint);
    };
    frame = requestAnimationFrame(paint);
    return () => cancelAnimationFrame(frame);
  }, []);

  return <canvas className="render-pulse" ref={ref} width={2} height={2} aria-hidden />;
}

export function Render() {
  const { slug = '' } = useParams();
  const [query] = useSearchParams();
  const { state } = useRealtime();

  useEffect(() => {
    document.title = RENDER_TITLE;
    document.body.dataset.render = 'on';
    return () => {
      delete document.body.dataset.render;
    };
  }, []);

  const params = useMemo<RecorderParams | null>(() => {
    const job = query.get('job');
    const token = query.get('token');
    if (!job || !token) return null;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = new URL(`${proto}://${location.host}/render-ingest`);
    url.searchParams.set('job', job);
    url.searchParams.set('token', token);
    return {
      ingestUrl: url.toString(),
      fps: Number(query.get('fps')) || 30,
      videoKbps: Number(query.get('v')) || 4500,
      audioKbps: Number(query.get('a')) || 128,
    };
    // Die Parameter stehen in der URL fest; ein Wechsel waere ein neuer Job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.get('job'), query.get('token')]);

  const approved = useMemo(() => state.sources.filter((s) => s.status === 'approved'), [state.sources]);
  const active = approved.find((s) => s.id === state.activeSourceId) ?? null;
  const overlay = approved.find((s) => s.id === state.overlaySourceId) ?? null;
  const wanted = useMemo(
    () => [active, overlay].filter((s): s is StreamSource => s != null),
    [active, overlay],
  );
  const { streams } = useSubscriptions(wanted);

  const mainStream = active ? streams[active.id] : null;
  const overlayStream = overlay ? streams[overlay.id] : null;
  const mainRef = useVideoStream(mainStream);
  const pipRef = useVideoStream(overlayStream);

  const recorder = useTabRecorder(params);
  /*
   * Ton und Bild des Ausgangs.
   *
   * Zwei Dinge koennen hier still bzw. schwarz sein: das Master-Stumm der
   * Regie, das fuer jede Quelle gilt, und der Schalter an der Quelle selbst.
   * Die Spuren einer abgeschalteten Quelle sind schon beim Sender aus -- das
   * hier ist die zweite Sicherung und, wichtiger, die Information, dass es
   * *absichtlich* still ist. Nur deshalb kann statt Schwarz das Standardbild
   * laufen.
   */
  /*
   * In der Werbepause laeuft das Live-Bild klein weiter, gehoert wird aber
   * nur der Spot. Das Master-Stumm der Regie gilt auch fuer ihn -- „Ton aus"
   * heisst: aus dem Ausgang kommt nichts.
   */
  const ad = useAdBreak(state.ads, state.adsAt);
  const adOn = Boolean(ad.active);
  const spotRef = useRef<HTMLVideoElement | null>(null);
  useMixedElement(recorder.mixer, spotRef, adOn && !state.output.muted);

  const audible = feedAudible(active?.feed, state.output, active?.kind) && !adOn;
  const blanked = Boolean(active) && !feedVisible(active?.feed, state.output);
  // Banner und L-Form: stumm, das Live-Bild rueckt nur zusammen -- sein Ton laeuft weiter.
  const frame = useAdFrame(state.ads, state.adsAt, adOn);
  const course = courseOf(state.event?.geofence);
  // Streckenkarte im Vollbild: das Hauptbild laeuft klein in der Ecke weiter, mit Ton.
  const courseFull = !adOn && courseFullscreen(state.widgets, state.ticker, course);
  const split = isSplit(state.pip, overlay) && !courseFull;
  // „Ganz zeigen" im Splitscreen: der Rand jeder Haelfte bekommt eine unscharfe Kopie.
  const fill = split && state.pip.fit === 'contain';

  /*
   * Was im Bild ist, ist auch zu hoeren: Hauptbild und kleines Fenster gehen
   * gemischt raus -- die Regie im kleinen Fenster kommentiert, waehrend ein
   * Handy das Rennen zeigt. Vorher lief nur das Hauptbild, und wer klein
   * geschaltet war, war im Stream stumm. Dieselbe Regel gilt bei den
   * Zuschauern (`pipAudible`).
   */
  useMixedAudio(recorder.mixer, mainStream, audible);
  useMixedAudio(recorder.mixer, overlayStream, pipAudible(overlay, active, state.output) && !adOn);

  return (
    <div className="render-stage">
      <div
        className="player"
        {...playerChrome({ pip: Boolean(overlay), course: courseFull }, state.widgets, state.pip)}
        {...backdropChrome(state.stage, state.event)}
        {...adChrome(ad, state.ads, Boolean(mainStream) && !blanked && !state.output.blanked)}
        {...adFrameChrome(frame)}
        {...courseChrome(courseFull, state.widgets.course, {
          live: Boolean(mainStream) && !blanked && !state.output.blanked,
          pipSlot: overlay && !split && !state.output.blanked ? state.pip.slot : null,
          logoSlot: sponsorBug(state.ads)?.config.slot ?? null,
        })}
      >
        <PlayerBackdrop stage={state.stage} event={state.event} />
        <div className="player__program">
          {fill && <SplitFill className="split-fill" stream={mainStream} blank={blanked} />}
          {/* Stumm: der Ton laeuft ueber den Mischer in die Aufnahme, nicht
              ueber die Ausgabe des Servers. */}
          <video
            ref={mainRef}
            className={blanked ? 'is-blank' : undefined}
            autoPlay
            playsInline
            muted
            style={videoLook(lookOf(state.cameras, active?.id))}
          />
          {/* Abgeblendet heisst Standardbild -- nicht Schwarz. Das unterscheidet
              eine Pause von einem Abbruch, auch fuer die Zuschauer bei YouTube. */}
          {(blanked || state.output.blanked) && <StandbyCard event={state.event} stage={state.stage} />}
          {/* Laut, aber nicht hoerbar: sein Ton geht in den Mischer (useMixedElement). */}
          <AdSpot ad={ad} muted={false} videoRef={spotRef} />
          <AdLabel ad={ad} live={Boolean(mainStream) && !blanked && !state.output.blanked} />
          {/* Die Master-Abblende nimmt auch das kleine Fenster mit: sie gilt fuer
              das Bild, nicht fuer eine einzelne Kamera. */}
          {overlay && !state.output.blanked && (
            <div className="pip">
              {fill && <SplitFill className="pip__fill" stream={overlayStream} blank={!camOn(overlay.feed)} />}
              <video
                ref={pipRef}
                className={camOn(overlay.feed) ? undefined : 'is-blank'}
                autoPlay
                playsInline
                muted
                style={videoLook(lookOf(state.cameras, overlay.id))}
              />
              <span className="pip__label">{overlay.label}</span>
            </div>
          )}
          {split && active && !state.output.blanked && <span className="split-label">{active.label}</span>}
          <StreamOverlay
            ticker={state.ticker}
            feed={state.tickerFeed}
            widgets={state.widgets}
            sport={state.event?.sport ?? 'sonstiges'}
            sponsor={sponsorBug(state.ads)}
            weather={state.weather}
            course={course}
            results={state.results}
            spotlight={state.spotlight}
          />
          {!mainStream && !blanked && !state.output.blanked && (
            <div className="render-idle">
              <b>{state.event?.title ?? 'Last Stream Standing'}</b>
              <span>{active ? 'Bild wird verbunden …' : 'Die Regie hat noch kein Hauptbild geschaltet.'}</span>
            </div>
          )}
        </div>
        <AdFrameLayer view={frame} ads={state.ads} />
      </div>

      <RenderPulse />

      {/* Nur fuer den Serverlog sichtbar -- der Renderer hat keine Zuschauer. */}
      <span hidden data-recorder={recorder.phase}>
        {recorder.error ?? ''}
      </span>
    </div>
  );
}
